import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler
} from "chart.js"

Chart.register(
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler
)

export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    type: { type: String, default: "bar" },
    data: String,
    label: { type: String, default: "" },
    currency: { type: String, default: "" },
    xType: { type: String, default: "" }
  }

  connect() {
    if (!this.hasCanvasTarget) return
    this.chart = this.createChart()
    this.themeObserver = new MutationObserver(() => this.rebuildChart())
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"]
    })
  }

  disconnect() {
    this.chart?.destroy()
    this.themeObserver?.disconnect()
  }

  rebuildChart() {
    this.chart?.destroy()
    this.chart = this.createChart()
  }

  createChart() {
    const isDark = document.documentElement.classList.contains("dark")
    const parsed = JSON.parse(this.dataValue || "{}")
    const labels = parsed.labels || []
    const datasets = (parsed.datasets || []).map((ds, i) => this.buildDataset(ds, i, isDark))

    const chartType = this.typeValue === "mixed" ? "bar" : this.typeValue
    const textColor = isDark ? "#9ca3af" : "#6b7280"
    const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"

    return new Chart(this.canvasTarget, {
      type: chartType,
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "index" },
        plugins: {
          legend: {
            display: datasets.length > 1,
            labels: { color: textColor, font: { family: "Inter", size: 12 } }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed.y
                if (this.currencyValue === "EUR") {
                  return `${ctx.dataset.label}: \u20AC${val.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                }
                return `${ctx.dataset.label}: ${val.toLocaleString()}`
              }
            }
          }
        },
        scales: {
          x: {
            ticks: {
              color: textColor,
              font: { family: "Inter", size: 11 },
              maxRotation: 45,
              callback: this.xTypeValue === "time" ? (value, index) => {
                const label = labels[index]
                if (!label) return label
                const d = new Date(label)
                if (isNaN(d.getTime())) return label
                return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit" })
              } : undefined
            },
            grid: { color: gridColor }
          },
          y: {
            ticks: {
              color: textColor,
              font: { family: "Inter", size: 11 },
              callback: (val) => {
                if (this.currencyValue === "EUR") return `\u20AC${val}`
                return val.toLocaleString()
              }
            },
            grid: { color: gridColor }
          }
        }
      }
    })
  }

  buildDataset(ds, index, isDark) {
    const colors = isDark
      ? ["rgba(59,130,246,0.7)", "rgba(16,185,129,0.7)", "rgba(139,92,246,0.7)"]
      : ["rgba(59,130,246,0.8)", "rgba(16,185,129,0.8)", "rgba(139,92,246,0.8)"]

    const borderColors = ["rgb(59,130,246)", "rgb(16,185,129)", "rgb(139,92,246)"]

    const isLine = (ds.type || this.typeValue) === "line"

    const base = {
      label: ds.label || "",
      data: ds.data || [],
      backgroundColor: ds.backgroundColor || colors[index % colors.length],
      borderColor: ds.borderColor || borderColors[index % borderColors.length],
      borderWidth: ds.borderWidth ?? (isLine ? 2 : 0),
      borderRadius: ds.borderRadius ?? (isLine ? 0 : 4),
      tension: ds.tension ?? 0.3,
      pointRadius: ds.pointRadius ?? (isLine ? 3 : 0),
      fill: ds.fill,
    }

    if (ds.type) base.type = ds.type
    return base
  }
}
